import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MD_DIALOG_REF, MDDialogModule, MDIconComponent } from '@melodic-ui/components';
import { TimezoneDropdownComponent } from '../timezone-dropdown/timezone-dropdown.component';
import { UserService } from '@lb/api/mylb/services/user.service';
import { TimezoneMigrationService } from '@lb/api/mylb/services/timezone-migration.service';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';
import { timezonesAreFunctionallyEquivalent } from '@lb/functions/browser-timezone.function';
import { switchMap, of, catchError, tap } from 'rxjs';
import { AccountStateService } from '@lb/state/account-state.service';

@Component({
	selector: 'lb-select-timezone-dialog',
	standalone: true,
	imports: [MDDialogModule, TimezoneDropdownComponent, MDIconComponent],
	templateUrl: './select-timezone-dialog.component.html',
	styleUrls: ['./select-timezone-dialog.component.scss']
})
export class SelectTimezoneDialogComponent {
	private readonly dialogRef = inject(MD_DIALOG_REF);
	private readonly userService = inject(UserService);
	private readonly timezoneMigrationService = inject(TimezoneMigrationService);
	private readonly globalTimezoneService = inject(GlobalTimezoneService);
	private readonly accountStateService = inject(AccountStateService);

	userID = input.required<number>();
	initialTimezoneID = input<number | null>(null);
	disableClose = input<boolean>(false);

	// Device (browser) timezone
	deviceTimezone = computed(() => this.globalTimezoneService.browserTimezone());

	// Full ITimeZone object for the dialog's current local selection
	selectedTimezoneObject = computed(() => {
		const id = this.selectedTimezone();
		if (id == null) return null;
		return this.globalTimezoneService.availableTimezones().find((tz) => tz.timeZoneID === id) ?? null;
	});

	// True when the dialog selection is functionally equivalent to the device timezone
	// (covers IANA aliases like America/Detroit ≡ America/New_York)
	isDeviceTimezoneSelected = computed(() => {
		const device = this.deviceTimezone();
		const selected = this.selectedTimezoneObject();
		if (!device || !selected) return false;
		return timezonesAreFunctionallyEquivalent(selected, device);
	});

	// Always shows the raw detected device timezone — never swaps to the user's selection.
	bannerTimezoneLabel = computed(() => {
		const tz = this.deviceTimezone();
		if (!tz) return '';
		return this.globalTimezoneService.formatTimezoneLabel(tz);
	});

	selectedTimezone = signal<number | null>(null);
	initialTimezoneSet = signal<boolean>(false);

	isConfirming = signal<boolean>(false);
	migrationError = signal<string | null>(null);
	constructor() {
		effect(() => this.trySetInitialTimezone());
	}

	onTimezoneChange(timeZoneID: unknown): void {
		this.selectedTimezone.set(timeZoneID as number);
	}

	useDeviceTimezone(): void {
		const tz = this.deviceTimezone();
		if (tz) {
			this.selectedTimezone.set(tz.timeZoneID);
		}
	}

	confirmTimezone(): void {
		const selectedTimezone = this.selectedTimezone();

		if (selectedTimezone == null) {
			return;
		}

		this.timezoneMigrationService
			.confirmTimezoneWithMigration(this.userID(), selectedTimezone)
			.pipe(
				switchMap((result) => {
					if (!result.success) {
						throw new Error(result.errorMessage || 'Migration failed');
					}
					// After successful migration, call regular update to get the updated timezone object
					return this.userService.updateTimezone(this.userID(), selectedTimezone);
				}),
				tap((tz) => {
					if (tz) {
						// Reload the account from API to get fresh hasCompletedTimezoneMigration flag
						// This ensures the dialog won't show again on page refresh
						this.accountStateService.reloadAccountFromApi();
					}
				}),
				catchError((error) => {
					console.error('Failed to update timezone with migration:', error);
					this.migrationError.set('Failed to update timezone. Please try again.');
					this.isConfirming.set(false);
					return of(null);
				})
			)
			.subscribe((tz) => {
				if (tz) {
					this.accountStateService.updateTimezone(tz);
					this.isConfirming.set(false);
					this.dialogRef.close(tz);
				} else {
					console.error('No timezone returned from update');
					this.isConfirming.set(false);
				}
			});
	}

	private trySetInitialTimezone(): void {
		// Only run once on initial load - don't reset when state changes
		if (this.initialTimezoneSet()) {
			return;
		}

		// Wait for timezones to load
		if (!this.globalTimezoneService.availableTimezones().length) {
			return;
		}

		// Only set if user hasn't already selected something
		if (this.selectedTimezone() != null) {
			this.initialTimezoneSet.set(true);
			return;
		}

		// Prefer an explicitly passed initialTimezoneID (e.g. when an admin opens the dialog
		// on behalf of another user). Fall back to the logged-in account's stored timezone,
		// then finally to the browser-detected timezone.
		const explicitTimezoneID = this.initialTimezoneID();
		const storedTimezone = this.accountStateService.user()?.timeZone;

		if (explicitTimezoneID) {
			this.selectedTimezone.set(explicitTimezoneID);
		} else if (storedTimezone?.timeZoneID) {
			this.selectedTimezone.set(storedTimezone.timeZoneID);
		} else {
			const browserTimezone = this.globalTimezoneService.browserTimezone();
			if (browserTimezone) {
				this.selectedTimezone.set(browserTimezone.timeZoneID);
			}
		}

		this.initialTimezoneSet.set(true);
	}
}
